import * as React from "react";
import { CSSProperties } from "react";
import colorsStore from "shared/store/colorsStore";
import { cn } from "utils/misc";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  placeholderStyle?: string;
}

const textareaStyle: CSSProperties = {
  "--textarea-caret-color": "#00549F",
  "--textarea-hover-border-color": "#00549F",
} as CSSProperties;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, placeholderStyle, style, ...props }, ref) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Combine refs
    const combinedRef = (node: HTMLTextAreaElement) => {
      // Forward the ref
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
      textareaRef.current = node;
    };

    const adjustHeight = () => {
      const textarea = textareaRef.current;
      if (textarea) {
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = "auto";
        // Set the height to match the content
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    React.useEffect(() => {
      adjustHeight();
    }, [props.value]);

    const placeholderCSS =
      placeholderStyle ||
      `
      textarea::placeholder {
        color: ${colorsConfig.Gray1};
        font-size: 16px;
        padding-left: 0; /* Ensure no extra padding on placeholder */
      }
      
      /* Added hover style */
      .custom-textarea:hover {
        border-color: ${colorsConfig.FinecoBlack || "#00549F"};
      }
      
      /* Remove focus shadow */
      .custom-textarea:focus {
        box-shadow: none;
        outline: none;
      }
    `;

    return (
      <>
        <style>{placeholderCSS}</style>
        <textarea
          className={cn(
            "custom-textarea dark:border-gray2F flex w-full border bg-white text-sm focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white",
            className,
          )}
          ref={combinedRef}
          style={{
            caretColor: "var(--textarea-caret-color)",
            resize: "none", // Disable manual resizing as we're handling it automatically
            minHeight: "40px", // Set a minimum height
            overflow: "hidden", // Hide scrollbars
            borderRadius: "6px",
            padding: props.disabled ? "12px 20px" : "12px 40px", // Consistent padding for text and placeholder
            ...(style as CSSProperties),
            backgroundColor: colorsConfig.white,
            color: colorsConfig.FinecoBlack,
            transition: "border-color 0.2s ease", // Added transition for smooth effect
          }}
          onChange={(e) => {
            adjustHeight();
            props.onChange?.(e);
          }}
          onInput={adjustHeight}
          {...props}
        />
      </>
    );
  },
);

Textarea.displayName = "Textarea";

export { Textarea, textareaStyle };
